/**
 * Phase 7.2 — Modal "Crear canonical_type custom".
 *
 * Allows admins to create up to 5 active custom canonical types beyond
 * the 6 system types. Validates name format live (lowercase alnum +
 * underscore, 2-32 chars starting with a letter) and forces an explicit
 * `stock_sign` choice (positive / negative / neutral) with a warning
 * about the cross-report impact.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Equal, AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { toast } from "sonner";
import inventoryReview from "../../api/inventoryReview";
import { formatApiError } from "../../api/client";

const NAME_RE = /^[a-z][a-z0-9_]{1,31}$/;

const SIGN_OPTIONS = [
  {
    value: "positive",
    icon: ArrowUp,
    iconClass: "text-emerald-500",
    label: "Positivo",
    desc: "Suma al stock (+qty). Ej: entrada, devolución.",
  },
  {
    value: "negative",
    icon: ArrowDown,
    iconClass: "text-rose-500",
    label: "Negativo",
    desc: "Resta del stock (-qty). Ej: salida, salida_orden.",
  },
  {
    value: "neutral",
    icon: Equal,
    iconClass: "text-slate-400",
    label: "Neutro",
    desc: "No afecta stock. Ej: transferencia interna.",
  },
];

export default function CreateCanonicalTypeModal({
  open,
  onOpenChange,
  onCreated,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [stockSign, setStockSign] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName("");
    setDisplayName("");
    setStockSign("");
    setSubmitting(false);
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const nameClean = name.trim().toLowerCase();
  const nameValid = NAME_RE.test(nameClean);
  const canSubmit =
    nameValid &&
    displayName.trim().length > 0 &&
    !!stockSign &&
    !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await inventoryReview.createCanonicalType({
        name: nameClean,
        display_name: displayName.trim(),
        stock_sign: stockSign,
      });
      toast.success(
        t("inventory.review.createTypeSuccess", {
          defaultValue: `Canonical type "${data.name}" creado`,
          name: data.name,
        }),
      );
      reset();
      onOpenChange(false);
      if (onCreated) onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid="create-canonical-type-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t("inventory.review.createTypeTitle", {
              defaultValue: "Crear canonical_type custom",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("inventory.review.createTypeSubtitle", {
              defaultValue:
                "Define una categoría nueva para reclasificar movimientos.",
            })}
          </DialogDescription>
        </DialogHeader>

        {/* Warning */}
        <Alert
          variant="default"
          className="border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/20 dark:border-amber-900/40"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-xs text-amber-700 dark:text-amber-200">
            {t("inventory.review.createTypeWarning", {
              defaultValue:
                "Este tipo aparecerá en TODOS los reportes y KPIs del Inventario. Verifica el signo antes de crear: una elección incorrecta puede sumar o restar mal al stock.",
            })}
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="ct-name" className="text-xs">
              {t("inventory.review.createTypeName", {
                defaultValue: "Nombre interno (key)",
              })}
            </Label>
            <Input
              id="ct-name"
              data-testid="create-ct-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ej. pick_pack"
              autoComplete="off"
              spellCheck={false}
            />
            <p
              className={`text-[11px] ${
                name && !nameValid ? "text-rose-500" : "text-muted-foreground"
              }`}
            >
              {t("inventory.review.createTypeNameHint", {
                defaultValue:
                  "Minúsculas, dígitos o underscore. 2-32 chars, empieza por letra.",
              })}
            </p>
          </div>

          {/* Display name */}
          <div className="space-y-1.5">
            <Label htmlFor="ct-display" className="text-xs">
              {t("inventory.review.createTypeDisplay", {
                defaultValue: "Nombre visible",
              })}
            </Label>
            <Input
              id="ct-display"
              data-testid="create-ct-display"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="ej. Pick & Pack"
              maxLength={64}
            />
          </div>

          {/* Stock sign */}
          <div className="space-y-1.5">
            <Label className="text-xs">
              {t("inventory.review.createTypeSign", {
                defaultValue: "Signo sobre stock",
              })}
            </Label>
            <div className="grid grid-cols-1 gap-2">
              {SIGN_OPTIONS.map((opt) => {
                const active = stockSign === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStockSign(opt.value)}
                    data-testid={`create-ct-sign-${opt.value}`}
                    className={`flex items-start gap-2 rounded-md border p-2.5 text-left text-xs transition-colors ${
                      active
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <Icon className={`h-4 w-4 mt-0.5 ${opt.iconClass}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-muted-foreground">{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
            data-testid="create-ct-cancel"
          >
            {t("common.cancel", { defaultValue: "Cancelar" })}
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="create-ct-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            {t("inventory.review.createTypeSubmit", {
              defaultValue: "Crear tipo",
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
